import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const TermsAndPrivacy: React.FC = () => {
  useEffect(() => {
    document.title = 'Terms & Privacy Policy - Ruhuna Engineering Faculty MRS';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src="/images/logo-uor.png" alt="University of Ruhuna" className="h-10 w-10 object-contain" />
            <img src="/images/logo-eng.png" alt="Faculty of Engineering" className="h-10 w-10 object-contain" />
            <div className="hidden sm:block">
              <p className="text-sm font-medium text-gray-900">University of Ruhuna</p>
              <p className="text-xs text-gray-500">Faculty of Engineering</p>
            </div>
          </div>
          <Link
            to="/signup"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Sign Up
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-10">

        {/* Title Block */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-8 text-center">
          <div className="flex justify-center items-center space-x-6 mb-6">
            <img src="/images/logo-uor.png" alt="University of Ruhuna" className="h-20 w-20 object-contain" />
            <img src="/images/logo-eng.png" alt="Faculty of Engineering" className="h-20 w-20 object-contain" />
          </div>
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">Terms and Regulations</h1>
          <p className="text-lg text-blue-700 font-medium">Maintenance &amp; Complaint Management System</p>
          <p className="text-base text-gray-600 mt-1">Faculty of Engineering - University of Ruhuna</p>
        </div>

        {/* Sections */}
        <div className="space-y-6">

          {/* Section 1 */}
          <Section number="1" title="Acceptance of Terms">
            <ClauseList items={[
              { num: '1.1', text: 'By accessing and using this digital Maintenance & Complaint Management System, I acknowledge that I have read, understood, and agreed to these Terms and Regulations.' },
              { num: '1.2', text: 'Continued use of this system constitutes acceptance of all applicable policies and institutional regulations.' },
            ]} />
          </Section>

          {/* Section 2 */}
          <Section number="2" title="Applicability of University and Faculty Bylaws">
            <ClauseList items={[
              { num: '2.1', text: 'I acknowledge that all University of Ruhuna and Faculty of Engineering bylaws, regulations, codes of conduct, and disciplinary procedures apply fully to this digital platform.' },
              { num: '2.2', text: 'I agree to adhere to all university and faculty rules while using this system.' },
              { num: '2.3', text: 'Any misuse of this system may result in disciplinary action in accordance with university regulations.' },
            ]} />
          </Section>

          {/* Section 3 */}
          <Section number="3" title="Eligibility and Authorized Use">
            <ClauseList items={[
              { num: '3.1', text: 'Access is restricted to authorized users (students, academic staff, administrative staff, and maintenance personnel).' },
              { num: '3.2', text: 'Users shall not share login credentials with unauthorized individuals.' },
              { num: '3.3', text: 'Users are responsible for maintaining the confidentiality of their account information.' },
            ]} />
          </Section>

          {/* Section 4 */}
          <Section number="4" title="Proper Use of the System">
            <ClauseList items={[
              { num: '4.1', text: 'The system shall only be used for legitimate maintenance and facility-related complaints.' },
            ]} />
            <p className="text-sm text-gray-600 mt-3 mb-2"><span className="font-medium text-gray-700">4.2</span> &nbsp;Users shall not:</p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 ml-6">
              <li>Submit false, misleading, or malicious complaints.</li>
              <li>Use abusive, offensive, defamatory, or inappropriate language.</li>
              <li>Upload harmful or unlawful content.</li>
              <li>Attempt to disrupt, hack, or manipulate the system.</li>
            </ul>
            <div className="mt-3">
              <ClauseList items={[
                { num: '4.3', text: 'Each complaint must contain accurate and truthful information.' },
              ]} />
            </div>
          </Section>

          {/* Section 5 */}
          <Section number="5" title="Complaint Handling and Status Updates">
            <ClauseList items={[
              { num: '5.1', text: 'Complaints will be reviewed and updated by authorized administrative personnel.' },
              { num: '5.2', text: 'Status updates reflect administrative actions and may not be modified once recorded.' },
              { num: '5.3', text: 'The system maintains a record of all status updates for transparency and accountability.' },
              { num: '5.4', text: 'Submission of a complaint does not guarantee immediate resolution but ensures formal tracking.' },
            ]} />
          </Section>

          {/* Section 6 - Privacy Policy */}
          <Section number="6" title="Privacy Policy and Data Protection" highlight>
            <ClauseList items={[
              { num: '6.1', text: 'The system collects personal data such as name, university affiliation, complaint details, and timestamps solely for operational and administrative purposes.' },
            ]} />
            <p className="text-sm text-gray-600 mt-3 mb-2"><span className="font-medium text-gray-700">6.2</span> &nbsp;Personal data will be processed in accordance with internationally recognized data protection principles, including:</p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 ml-6">
              <li>Lawfulness, fairness, and transparency</li>
              <li>Purpose limitation</li>
              <li>Data minimization</li>
              <li>Accuracy</li>
              <li>Storage limitation</li>
              <li>Integrity and confidentiality</li>
            </ul>
            <p className="text-sm text-gray-600 mt-3 mb-2"><span className="font-medium text-gray-700">6.3</span> &nbsp;Data handling practices aim to align with international privacy standards such as:</p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 ml-6">
              <li>General Data Protection Regulation (GDPR) principles</li>
              <li>ISO/IEC 27001 information security guidelines</li>
            </ul>
            <div className="mt-3">
              <ClauseList items={[
                { num: '6.4', text: 'Personal data will not be shared with third parties unless required by university administration or legal obligations.' },
                { num: '6.5', text: 'Appropriate technical and organizational measures will be taken to protect data from unauthorized access, alteration, or disclosure.' },
              ]} />
            </div>
          </Section>

          {/* Section 7 */}
          <Section number="7" title="User Rights">
            <p className="text-sm text-gray-600 mb-3">Users have the right to:</p>
            <ClauseList items={[
              { num: '7.1', text: 'Access their submitted complaints and status history.' },
              { num: '7.2', text: 'Request correction of inaccurate personal information.' },
              { num: '7.3', text: 'Request clarification regarding complaint handling procedures.' },
              { num: '7.4', text: 'Expect confidentiality and fair treatment.' },
            ]} />
          </Section>

          {/* Section 8 */}
          <Section number="8" title="System Monitoring">
            <ClauseList items={[
              { num: '8.1', text: 'The system may log activities for security, auditing, and administrative purposes.' },
              { num: '8.2', text: 'The Faculty reserves the right to monitor usage to ensure compliance with regulations.' },
            ]} />
          </Section>

          {/* Section 9 */}
          <Section number="9" title="Limitation of Liability">
            <p className="text-sm text-gray-600 mb-2"><span className="font-medium text-gray-700">9.1</span> &nbsp;The Faculty of Engineering shall not be liable for:</p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 ml-6">
              <li>Delays in complaint resolution.</li>
              <li>Temporary system unavailability.</li>
              <li>Data loss caused by force majeure or technical failures beyond reasonable control.</li>
            </ul>
          </Section>

          {/* Section 10 */}
          <Section number="10" title="Amendments">
            <ClauseList items={[
              { num: '10.1', text: 'The Faculty reserves the right to modify these Terms and Regulations at any time.' },
              { num: '10.2', text: 'Continued use of the system after modifications constitutes acceptance of revised terms.' },
            ]} />
          </Section>

          {/* Section 11 */}
          <Section number="11" title="Governing Authority">
            <ClauseList items={[
              { num: '11.1', text: 'These Terms and Regulations shall be governed by the rules and administrative authority of the University of Ruhuna and the applicable laws of Sri Lanka.' },
            ]} />
          </Section>

        </div>

        {/* Footer CTA */}
        <div className="mt-10 bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-center">
          <p className="text-sm text-gray-600 mb-4">
            By creating an account, you confirm that you have read and agree to these Terms and Regulations.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-6 py-2.5 rounded transition-colors"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Return to Sign Up
          </Link>
        </div>

        {/* Page Footer */}
        <div className="mt-8 pb-6 flex justify-between items-center text-xs text-gray-400">
          <span>© 2026 University of Ruhuna - Faculty of Engineering</span>
          <a
            href="https://scorpion-xweb.vercel.app/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 hover:text-blue-600 transition-colors font-medium"
          >
            Powered by Scorpion X
          </a>
        </div>
      </div>
    </div>
  );
};

/* ── Helpers ─────────────────────────────────────────────── */

interface SectionProps {
  number: string;
  title: string;
  children: React.ReactNode;
  highlight?: boolean;
}

const Section: React.FC<SectionProps> = ({ number, title, children, highlight }) => (
  <div className={`bg-white rounded-xl border shadow-sm p-6 ${highlight ? 'border-blue-200' : 'border-gray-200'}`}>
    <div className="flex items-start gap-3 mb-4">
      <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${highlight ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
        {number}
      </span>
      <h2 className="text-base font-semibold text-gray-900 pt-1">{title}</h2>
    </div>
    <div className="ml-11">{children}</div>
  </div>
);

interface ClauseItem {
  num: string;
  text: string;
}

const ClauseList: React.FC<{ items: ClauseItem[] }> = ({ items }) => (
  <div className="space-y-2">
    {items.map(({ num, text }) => (
      <p key={num} className="text-sm text-gray-600">
        <span className="font-medium text-gray-700">{num}</span> &nbsp;{text}
      </p>
    ))}
  </div>
);

export default TermsAndPrivacy;
